import React, { type JSX, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import SystemTypeRender from '../SystemTypeRender';
import { useRuntimeConfig } from 'vl-common/src/hooks/Runtime';
import { useSystemType } from 'vl-common/src/context/provider.app';
import styled from '@emotion/styled';
import logRocketLogo from '../../../assets/img/logRocket.png';
import UIVersionLabel from '../UIVersionLabel';
import { HarFunctionsType, useHARFunctions } from 'vl-common/src/hooks/useHARFunctions';
import TestingModal from './TestingModal';
import DumpModal from './DumpModal';
import LocaleSwitcher from './LocaleSwitcher';

const hbsukLogo = 'https://assets.hbsuk.co.uk/img/platform/HBSUK_Vertical_Logo_Colour_RGB.svg';

const StyledFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  background-color: #fff;
  border-top: solid 1px var(--productIron);
  padding: 5px 14px;
  font-size: 14px;

  width: 100%;

  &.extreme-zoom {
    zoom: 0.5;
  }
`;

export const CoreFooter: React.FC<{
  logRocketSessionURL?: string;
  ThemeButton?: () => JSX.Element;
  AccessibilityContainer?: () => JSX.Element;
  footerClass?: string;
  harProps: HarFunctionsType;
  showCypressButton?: boolean;
}> = ({
  logRocketSessionURL,
  ThemeButton = () => <></>,
  AccessibilityContainer = () => <></>,
  footerClass = '',
  harProps,
  showCypressButton = false
}) => {
  const { t } = useTranslation('common');
  const { LUCY_ENV, DATABASE_VERSION } = useRuntimeConfig();
  const [type] = useSystemType();
  const isProd = LUCY_ENV.includes('prod');
  const footerRef = useRef<HTMLDivElement>(null);

  const [devToolsIsVisible, setDevToolsIsVisible] = useState(true);

  const {
    clearButtonProps,
    dumpButtonProps,
    recordButtonProps,
    testingButtonProps,
    disableButtonProps,
    harIsRunning,
    enterTestingMode,
    exitTestingMode,
    inTestingMode,
    setInTestingMode,
    testingModalOpen,
    setTestingModalOpen,
    dumpModalOpen,
    setDumpModalOpen,
    dumpedHAR
  } = harProps;

  const systemTypeLabels = {
    admin: '(HBS) client admin',
    referrer: '(client) referrer',
    'referrer-admin': '(client) referrer admin',
    clinician: 'clinician',
    patient: 'patient'
  };
  const systemTypeLabel = systemTypeLabels[type as keyof typeof systemTypeLabels];

  useEffect(() => {
    const footer = footerRef.current;
    if (!footer) return;

    const observer = new ResizeObserver(([entry]) => {
      const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
      document.documentElement.style.setProperty('--footer-height', `${height}px`);
    });

    observer.observe(footer);
    return () => observer.disconnect();
  }, []);

  const logo = hbsukLogo as string | { src: string };

  return (
    <StyledFooter className={footerClass} ref={footerRef}>
      <span>
        <UIVersionLabel />
      </span>
      <img
        src={
          // a (hopefully) temporary measure to accomodate Next’s Webpack image loader
          typeof logo === 'string' ? logo : logo.src
        }
        alt={t('HBS UK logo')}
        width="40px"
      />{' '}
      <SystemTypeRender APP>
        <a
          rel="noreferrer"
          target="_blank"
          href="/patients/legal/?all_policies=0&activeTab=USAGE"
          tabIndex={0}
          data-testid="terms"
        >
          {t('Terms')}
        </a>
        <a rel="noreferrer" target="_blank" href="/accessibility" tabIndex={0} data-testid="accessibility">
          {t('Accessibility')}
        </a>
        <a rel="noreferrer" target="_blank" href="/regulatory" tabIndex={0} data-testid="regulatory">
          <img src="/static/images/ukca.png" alt={t('UKCA Mark (UK Conformity Assessed)')} width="25px" />
        </a>
      </SystemTypeRender>
      <SystemTypeRender ADMIN>
        {!isProd && (
          <span>
            &nbsp;Type: {systemTypeLabel}, &quot;{type}&quot;
          </span>
        )}
      </SystemTypeRender>
      {!isProd && devToolsIsVisible && (
        <>
          {t('Dev Tools:')}{' '}
          {logRocketSessionURL && (
            <a href={logRocketSessionURL} target="_blank" data-testid="logrocket" rel="noreferrer">
              <img
                src={
                  // a (hopefully) temporary measure to accomodate Next’s Webpack image loader
                  typeof logRocketLogo === 'string' ? logRocketLogo : logRocketLogo.src
                }
                width="30px"
                alt={t('LogRocket')}
              />
            </a>
          )}
          <ThemeButton data-testid="theme-toggle" />
          <LocaleSwitcher />
          <div data-testid="db-version">
            {t('DB Version:')}
            {DATABASE_VERSION}
          </div>
          <Button data-testid="hide-dev-footer" onClick={() => setDevToolsIsVisible(false)} size="small">
            {t('Hide')}
          </Button>
          <Button {...clearButtonProps.props} data-testid="footer-clear-button">
            {clearButtonProps.text}
          </Button>
          <Button {...recordButtonProps.props} data-testid="footer-recrod-button">
            {recordButtonProps.text}
          </Button>
          <Button {...dumpButtonProps.props} data-testid="footer-dumo-button">
            {dumpButtonProps.text}
          </Button>
          <Button {...testingButtonProps.props} data-testid="footer-testing-button">
            {testingButtonProps.text}
          </Button>
          {harIsRunning && (
            <Button {...disableButtonProps.props} data-testid="footer-har-button">
              {disableButtonProps.text}
            </Button>
          )}
        </>
      )}
      {!isProd && !devToolsIsVisible && (
        <Button
          onClick={() => setDevToolsIsVisible(true)}
          className="dev-tools-item"
          size="small"
          data-testid="show-dev-tools"
        >
          {t('Show dev tools')}
        </Button>
      )}
      <AccessibilityContainer />
      <TestingModal
        visible={testingModalOpen}
        onCancel={() => {
          setTestingModalOpen(false);
        }}
        enterTestingMode={enterTestingMode}
        exitTestingMode={exitTestingMode}
        inTestingMode={inTestingMode}
        setInTestingMode={setInTestingMode}
      />
      <DumpModal
        visible={dumpModalOpen}
        har={dumpedHAR || undefined}
        onCancel={() => {
          setDumpModalOpen(false);
        }}
      />
      {showCypressButton ? (
        <Button size="small" data-testid="copy-cypress-script" onClick={() => window.__cyRecorder?.copy()}>
          Copy Cypress script
        </Button>
      ) : null}
    </StyledFooter>
  );
};

const Footer: React.FC<{
  className?: string;
  logRocketSessionURL?: string;
  getDynamicConfig: any;
  ThemeButton?: () => JSX.Element;
  footerClass?: string;
  showCypressButton?: boolean;
}> = ({
  className,
  logRocketSessionURL,
  getDynamicConfig,
  ThemeButton = () => <></>,
  footerClass,
  showCypressButton = false
}) => {
  const harProps = useHARFunctions(getDynamicConfig);

  return (
    <footer data-testid="footer" {...{ className }}>
      <CoreFooter
        logRocketSessionURL={logRocketSessionURL}
        ThemeButton={ThemeButton}
        footerClass={footerClass}
        harProps={harProps}
        showCypressButton={showCypressButton}
      />
    </footer>
  );
};

export default Footer;
